import { showToast } from '../Notifications/notificationsSlice';
import {Group} from "../groups/groupsService";
import {Msisdn} from "../Msisdns/msisdnService";
import {api} from "../../api.ts";
import {unwrapData} from "../../apiResponse.ts";


export interface UserSettings {
    userId: string | null,
    firstName: string,
    lastName: string,
    position: string,
    role?: string,
    email: string,
    missedCallEmailNotificationEnabled: boolean,
    groups: Group[],
    msisdns: Msisdn[],
    avatarUrl: string,
    userTimeZone: string,
}
interface UserPersonalizationSettings {
    userId: number,
    googleCalendarSettingsResponse: GoogleCalendarSettingsResponse
}
interface GoogleCalendarSettingsResponse {
    updateMeetingsStatus: boolean;
    updateDndOnMeetings: boolean;
    integrationActive?: boolean;
}

interface MissedCallsNotifications {
    missedCallEmailNotificationEnabled: boolean;
    phoneNumberNotificationSettings: phoneNumberNotificationSettings[];
    userId: number;
}

export interface PhoneNumber {
    id: number,
    assigmentScope?: string,
    phoneNumber: number,
    label: string,
    icon: string,
    type: string,
    iconColor: string
}

export interface phoneNumberNotificationSettings {
    missedCallEmailNotificationEnabled: boolean;
    phoneNumber: PhoneNumber;
}
export const profileSettingsApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getUserProfile: builder.query<UserSettings, string | null>({
            query: (userId) => ({
                url: `/api/v1/users/${userId}/profile-settings`,
            }),
            transformResponse: (response: { data: UserSettings }) => unwrapData(response),
            providesTags: ['Profile'],
        }),
        getUserProfileNotifications: builder.query<MissedCallsNotifications, string | null>({
            query: (userId) => ({
                url: `/api/v1/users/${userId}/profile-settings/notifications`,
            }),
            transformResponse: (response: { data: MissedCallsNotifications }) => unwrapData(response),
            providesTags: ['Notifications']
        }),
        updateUserProfile: builder.mutation<void, { data: Partial<UserSettings>, userId: string | null }>({
            query: ({data, userId}) => ({
                url: `/api/v1/users/${userId}/profile-settings`,
                method: 'PATCH',
                body: {...data, userId},
            }),
            async onQueryStarted( {},{ dispatch, queryFulfilled }) {
                try {

                    await queryFulfilled;
                    dispatch(showToast({ description: 'Profile updated successfully!', type: 'success' }));
                } catch {
                    dispatch(showToast({ description: 'Failed to update profile!', type: 'error' }));
                }
            },
            invalidatesTags: ['Profile'],
        }),
        updateUserProfileNotifications: builder.mutation<void, { userId: string; missedCallEmailNotificationEnabled: boolean,disabledMissedCallEmailNotificationPhoneNumberIds: number[] }>({
            query: ({ userId, missedCallEmailNotificationEnabled, disabledMissedCallEmailNotificationPhoneNumberIds }) => ({
                url: `/api/v1/users/${userId}/profile-settings/notifications`,
                method: 'PATCH',
                body: {userId, missedCallEmailNotificationEnabled, disabledMissedCallEmailNotificationPhoneNumberIds },
            }),
            async onQueryStarted({  }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(showToast({ description: 'Notifications state updated successfully!', type: 'success' }));
                } catch {
                    dispatch(showToast({ description: 'Failed to change notifications state!', type: 'error' }));
                }
            },
            invalidatesTags: ['Notifications'],

        }),

        updateUserTimezone: builder.mutation<void, { userId: string | null ; userTimeZone: string }>({
            query: ({userId, userTimeZone}) => ({
                url: `/api/v1/users/${userId}/timezone`,
                method: 'PATCH',
                body: {userTimeZone},
            }),
            invalidatesTags: ['Profile'],
        }),
        getGoogleCalendarSettings: builder.query<UserPersonalizationSettings, string | null>({
            query: (userId) => ({
                url: `/api/v1/users/${userId}/profile-settings/personalization`,
            }),
            transformResponse: (response: { data: UserPersonalizationSettings }) => unwrapData(response),
            providesTags: ['CalendarSettings'],
        }),
        patchGoogleCalendarSettings: builder.mutation<void, { userId: number; updateMeetingsStatus: boolean | undefined, updateDndOnMeetings: boolean | undefined }>({
            query: ({userId, updateMeetingsStatus, updateDndOnMeetings} ) => ({
                url: `/api/v1/users/${userId}/profile-settings/personalization`,
                method: 'PATCH',
                body:  {userId, updateMeetingsStatus, updateDndOnMeetings} ,
            }),
            async onQueryStarted({}, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(showToast({ description: 'Google Calendar settings updated successfully!', type: 'success' }));
                } catch {
                    dispatch(showToast({ description: 'Failed to update Google Calendar settings!', type: 'error' }));
                }
            },
            invalidatesTags: ['CalendarSettings'],
        })
    }),
});


export const {
    useGetUserProfileQuery,
    useLazyGetUserProfileQuery,
    useUpdateUserProfileMutation,
    useGetUserProfileNotificationsQuery,
    useUpdateUserProfileNotificationsMutation,
    useUpdateUserTimezoneMutation,
    useGetGoogleCalendarSettingsQuery,
    usePatchGoogleCalendarSettingsMutation,
} = profileSettingsApi;
